/**
 * StudioImageSuggestions — Interactive image suggestion panel
 *
 * Shown after beautification pauses at 'suggestions_ready'.
 * Users can edit prompts, generate images individually, toggle
 * selection, and trigger final document assembly.
 */

import { useState, useCallback } from 'react'
import {
  Image,
  Wand2,
  Download,
  Loader2,
  Check,
  RefreshCw,
  FileDown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'
import type { StudioVisualization, StudioVizType, StudioVizStatus } from '@/lib/api'

interface Props {
  sessionId: string
  visualizations: StudioVisualization[]
  pipelineStatus: string
  onVisualizationsChange: (viz: StudioVisualization[]) => void
  onAssembleComplete: () => void
}

const TYPE_BADGES: Record<StudioVizType, { label: string; className: string }> = {
  chart: { label: 'Chart', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  infographic: { label: 'Infographic', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  mermaid: { label: 'Diagram', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
}

const STATUS_INDICATORS: Record<StudioVizStatus, { label: string; className: string }> = {
  suggested: { label: 'Ready to generate', className: 'text-gray-400' },
  generating: { label: 'Generating...', className: 'text-blue-500 animate-pulse' },
  generated: { label: 'Generated', className: 'text-green-500' },
  failed: { label: 'Failed', className: 'text-red-500' },
}

export function StudioImageSuggestions({
  sessionId,
  visualizations,
  pipelineStatus,
  onVisualizationsChange,
  onAssembleComplete,
}: Props) {
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set())
  const [assembling, setAssembling] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const updateViz = useCallback((vizId: string, updates: Partial<StudioVisualization>) => {
    onVisualizationsChange(
      visualizations.map(v => v.id === vizId ? { ...v, ...updates } : v)
    )
  }, [visualizations, onVisualizationsChange])

  const handleToggleSelected = useCallback(async (vizId: string) => {
    const viz = visualizations.find(v => v.id === vizId)
    if (!viz) return
    const newSelected = !viz.selected
    updateViz(vizId, { selected: newSelected })
    try {
      await api.studio.updateViz(sessionId, vizId, { selected: newSelected })
    } catch {
      // Revert on error
      updateViz(vizId, { selected: !newSelected })
    }
  }, [sessionId, visualizations, updateViz])

  const handlePromptBlur = useCallback(async (vizId: string, newPrompt: string) => {
    const viz = visualizations.find(v => v.id === vizId)
    if (!viz || viz.imagePrompt === newPrompt) return
    updateViz(vizId, { imagePrompt: newPrompt })
    try {
      await api.studio.updateViz(sessionId, vizId, { imagePrompt: newPrompt })
    } catch {
      notify.error('Failed to save prompt')
    }
  }, [sessionId, visualizations, updateViz])

  const handleGenerate = useCallback(async (vizId: string) => {
    setGeneratingIds(prev => new Set(prev).add(vizId))
    updateViz(vizId, { status: 'generating' })

    try {
      const result = await api.studio.generateViz(sessionId, vizId)
      updateViz(vizId, {
        status: result.status,
        pngBase64: result.pngBase64,
      })
      if (result.status === 'generated') {
        notify.success(`${result.title} generated`)
      } else {
        notify.error(`${result.title} generation failed`)
      }
    } catch (err) {
      updateViz(vizId, { status: 'failed' })
      notify.error('Generation failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setGeneratingIds(prev => {
        const next = new Set(prev)
        next.delete(vizId)
        return next
      })
    }
  }, [sessionId, updateViz])

  const handleGenerateAll = useCallback(async () => {
    const pending = visualizations.filter(v =>
      (v.selected ?? true) && (v.status === 'suggested' || v.status === 'failed')
    )
    if (pending.length === 0) return

    for (const viz of pending) {
      setGeneratingIds(prev => new Set(prev).add(viz.id))
      updateViz(viz.id, { status: 'generating' })

      try {
        const result = await api.studio.generateViz(sessionId, viz.id)
        updateViz(viz.id, { status: result.status, pngBase64: result.pngBase64 })
      } catch {
        updateViz(viz.id, { status: 'failed' })
      } finally {
        setGeneratingIds(prev => {
          const next = new Set(prev)
          next.delete(viz.id)
          return next
        })
      }
    }

    const generated = pending.filter(v => v.status !== 'failed').length
    if (generated > 0) notify.success(`${generated} image${generated !== 1 ? 's' : ''} generated`)
  }, [sessionId, visualizations, updateViz])

  const handleAssemble = useCallback(async () => {
    const selectedGenerated = visualizations.filter(v => (v.selected ?? true) && v.status === 'generated')
    if (selectedGenerated.length === 0) {
      notify.error('No generated images selected')
      return
    }

    setAssembling(true)
    try {
      await api.studio.assemble(sessionId)
      notify.success('Document assembled with images!')
      onAssembleComplete()
    } catch (err) {
      notify.error('Assembly failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setAssembling(false)
    }
  }, [sessionId, visualizations, onAssembleComplete])

  const handleDownload = useCallback((vizId: string) => {
    const url = api.studio.vizPngUrl(sessionId, vizId)
    const a = document.createElement('a')
    a.href = url
    a.download = `${vizId}.png`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [sessionId])

  if (visualizations.length === 0) return null

  const hasSelectedGenerated = visualizations.some(v => (v.selected ?? true) && v.status === 'generated')
  const pendingCount = visualizations.filter(v => (v.selected ?? true) && (v.status === 'suggested' || v.status === 'failed')).length
  const isGeneratingAny = generatingIds.size > 0
  const isCompleted = pipelineStatus === 'completed'

  return (
    <div className="border-t border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-surface-900/50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Suggested Images
          </h3>
          <span className="text-xs text-gray-400">
            {visualizations.length} suggestion{visualizations.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {!isCompleted && pendingCount > 0 && (
            <button
              onClick={handleGenerateAll}
              disabled={isGeneratingAny}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isGeneratingAny ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Wand2 className="w-3.5 h-3.5" />
              )}
              {isGeneratingAny ? 'Generating...' : `Generate All (${pendingCount})`}
            </button>
          )}

          {!isCompleted && (
            <button
              onClick={handleAssemble}
              disabled={!hasSelectedGenerated || assembling}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {assembling ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5" />
              )}
              {assembling ? 'Assembling...' : 'Assemble Document'}
            </button>
          )}
        </div>
      </div>

      {/* Visualization cards */}
      <div className="divide-y divide-gray-200 dark:divide-white/10">
        {visualizations.map((viz) => {
          const typeBadge = TYPE_BADGES[viz.type] || { label: viz.type || 'Image', className: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' }
          const statusInfo = STATUS_INDICATORS[viz.status] || STATUS_INDICATORS.suggested
          const isGenerating = generatingIds.has(viz.id)
          const isExpanded = expandedId === viz.id

          return (
            <div key={viz.id} className="px-4 py-3">
              {/* Top row: checkbox, type badge, title, status, actions */}
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <label className="flex items-center mt-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={viz.selected ?? true}
                    onChange={() => handleToggleSelected(viz.id)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-white/20 dark:bg-white/5"
                  />
                </label>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${typeBadge.className}`}>
                      {typeBadge.label}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {viz.title}
                    </span>
                    <span className={`text-xs ${statusInfo.className}`}>
                      {statusInfo.label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                    {viz.description}
                  </p>

                  {/* Expandable prompt editor */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : viz.id)}
                    className="text-xs text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 mb-1"
                  >
                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {isExpanded ? 'Hide prompt' : 'Edit prompt'}
                  </button>

                  {isExpanded && (
                    <textarea
                      defaultValue={viz.imagePrompt}
                      onBlur={(e) => handlePromptBlur(viz.id, e.target.value)}
                      rows={3}
                      className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white resize-y focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Describe how this visualization should look..."
                    />
                  )}

                  {/* Generated image thumbnail */}
                  {viz.status === 'generated' && viz.pngBase64 && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 inline-block">
                      <img
                        src={`data:image/png;base64,${viz.pngBase64}`}
                        alt={viz.title}
                        className="max-h-48 w-auto"
                      />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Download button (shown when image exists) */}
                  {viz.status === 'generated' && (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <button
                        onClick={() => handleDownload(viz.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Download PNG"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}

                  {/* Generate / Regenerate button (always visible except during generation) */}
                  {viz.status === 'generating' || isGenerating ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-500">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {viz.status === 'generating' ? 'Generating' : ''}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleGenerate(viz.id)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                        viz.status === 'generated'
                          ? 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50'
                      }`}
                      title={viz.status === 'generated' ? 'Regenerate image' : 'Generate image'}
                    >
                      {viz.status === 'generated' || viz.status === 'failed' ? (
                        <RefreshCw className="w-3 h-3" />
                      ) : (
                        <Wand2 className="w-3 h-3" />
                      )}
                      {viz.status === 'generated' ? 'Redo' : viz.status === 'failed' ? 'Retry' : 'Generate'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
